// src/pages/Client/ClientRecipesManagerPage.js (UPDATED)

import React, { useState, useEffect, useCallback } from 'react';
import { db, auth } from '../../firebase';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import RecipeFormModal from '../../components/RecipeFormModal';
import RecipeDetailModal from '../../components/RecipeDetailModal'; 
import '../../styles/ClientRecipes.css'; 

const ClientRecipesManagerPage = () => {
    const [recipes, setRecipes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false); // For Add New Recipe form
    
    // State for the Detail Modal
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [selectedRecipe, setSelectedRecipe] = useState(null);

    const [clientUID, setClientUID] = useState(null);

    // Function to fetch recipes
    const fetchRecipes = useCallback(async (uid) => {
        if (!uid) return;
        setLoading(true);
        try {
            const recipesRef = collection(db, 'recipes', uid, 'uploadedRecipes');
            const q = query(recipesRef, orderBy('uploadedAt', 'desc'));
            const querySnapshot = await getDocs(q);
            
            const fetchedRecipes = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                // Convert Firestore Timestamp to Date for the detail modal
                uploadedAt: doc.data().uploadedAt 
            }));

            setRecipes(fetchedRecipes);
        } catch (error) {
            console.error("Error fetching recipes: ", error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Effect for initial authentication and data fetch
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setClientUID(user.uid);
                fetchRecipes(user.uid);
            } else {
                setClientUID(null);
                setLoading(false);
            }
        });
        return () => unsubscribe();
    }, [fetchRecipes]);

    // Handler to close upload modal and re-fetch data
    const handleFormSubmitSuccess = () => {
        setIsUploadModalOpen(false);
        if (clientUID) {
            fetchRecipes(clientUID);
        }
    };
    
    // Handler to open Detail Modal when a card is clicked
    const handleCardClick = (recipe) => {
        setSelectedRecipe(recipe);
        setIsDetailModalOpen(true);
    };

    // Recipe Card Component
    const RecipeCard = ({ recipe }) => (
        <div className="recipe-card" onClick={() => handleCardClick(recipe)}>
            <h3 className="recipe-name">🍽️ {recipe.name}</h3>
            <p className="recipe-description">{recipe.description}</p>
            <div className="recipe-details-toggle">
                {/* Use optional chaining for safety when checking array length */}
                <p><strong>Ingredients:</strong> {recipe.ingredients?.length || 0} items</p>
                <span className="view-details-prompt">Click to view full recipe</span> 
            </div>
            <span className="recipe-uploaded-date">
                {recipe.uploadedAt && new Date(recipe.uploadedAt.toDate()).toLocaleDateString()}
            </span>
        </div>
    );

    return (
        <div className="client-recipes-manager-page">
            <div className="manager-header">
                <h1 className="page-title">Recipe Library</h1>
                <button 
                    className="add-recipe-button" 
                    onClick={() => setIsUploadModalOpen(true)}
                >
                    ➕ Add New Recipe
                </button>
            </div>
            
            <p className="page-subtitle">Recipes uploaded by you for your clients.</p>

            <div className="recipes-list-container">
                {loading && <p className="status-message">Loading recipes...</p>}
                
                {!loading && recipes.length === 0 && (
                    <div className="empty-state-card">
                        <p>No recipes found. Click "Add New Recipe" to get started!</p>
                    </div>
                )}
                
                {recipes.map(recipe => (
                    <RecipeCard key={recipe.id} recipe={recipe} />
                ))}
            </div>

            {/* Recipe Form Modal (for adding/uploading) */}
            <RecipeFormModal 
                isOpen={isUploadModalOpen} 
                onClose={() => setIsUploadModalOpen(false)} 
                clientUID={clientUID}
                onSuccess={handleFormSubmitSuccess}
            />
            
            {/* Recipe Detail Modal (for viewing full recipe) */}
            <RecipeDetailModal
                isOpen={isDetailModalOpen}
                onClose={() => setIsDetailModalOpen(false)}
                recipe={selectedRecipe}
            />
        </div>
    );
};

export default ClientRecipesManagerPage;